/**
 * #976: the live adapters for #978's notes and #979's sources. Each
 * implements the consumer's adapter interface exactly and throws the
 * consumer's own refusal class:
 *
 *   const client = createCardAnnotationClient(browserCardAnnotationTransport);
 *   const notes = createCardNoteAdapter(client, { onUnavailable });
 *   const sources = createCardSourceAdapter(client, { onUnavailable });
 *
 * No cache, poller, retry or localStorage: the consumers' controllers own
 * when to read, and they fence late replies by generation as well.
 *
 * Targets the server cannot tie to an explicit venue account are left out of
 * read results (the consumer shows them empty) and reported through the
 * `onUnavailable` hook; a save to one is refused as `forbidden`.
 * `pageScopeFor` hands out the partial token; the controls disable cards from
 * `onUnavailable` (see availability.ts).
 */
import { CardNoteSaveError, type CardNoteAdapter } from '../cardNotes/types';
import { CardSourceWriteError, type CardSourceAdapter } from '../cardSources/types';
import { CardAnnotationClientError, type CardAnnotationClient } from './client';
import type { CardAvailabilityOptions, CardScopeResult, CardTarget, CardVenue } from './types';

/**
 * A write refused with CARD_TARGET_UNAVAILABLE says this ONE card is not provable for the caller
 * (a fill through another key landed after the first read). The token did not change, so a scope
 * re-read cannot disable it; report it through the same hook as a read does.
 */
function reportRefusedTarget(error: CardAnnotationClientError, target: CardTarget, options: CardAvailabilityOptions) {
  if (error.code === 'CARD_TARGET_UNAVAILABLE') options.onUnavailable?.([{ target, reason: 'no_exact_account_identity' }]);
}

export function createCardNoteAdapter(client: CardAnnotationClient, options: CardAvailabilityOptions = {}): CardNoteAdapter {
  return {
    async read(targets) {
      if (targets.length === 0) return [];
      const { records, unavailable } = await client.readNotes(targets);
      if (unavailable.length > 0) options.onUnavailable?.(unavailable);
      return records;
    },
    async save(target, note, expectedRevision) {
      try {
        return await client.saveNote(target, note, expectedRevision);
      } catch (error) {
        if (error instanceof CardAnnotationClientError && error.status === 409) {
          const latest = client.conflictNote(error, target);
          if (latest) throw new CardNoteSaveError('conflict', latest);
        }
        if (error instanceof CardAnnotationClientError && error.status === 403) {
          reportRefusedTarget(error, target, options);
          throw new CardNoteSaveError('forbidden');
        }
        throw error;
      }
    },
  };
}

export function createCardSourceAdapter(client: CardAnnotationClient, options: CardAvailabilityOptions = {}): CardSourceAdapter {
  return {
    listSources: () => client.listSources(),
    async createSource(name) {
      try {
        return await client.createSource(name);
      } catch (error) {
        if (error instanceof CardAnnotationClientError && error.code === 'CARD_TEXT_INVALID') throw new CardSourceWriteError('invalid_name');
        if (error instanceof CardAnnotationClientError && error.status === 403) throw new CardSourceWriteError('forbidden');
        throw error;
      }
    },
    async readAssignments(targets) {
      if (targets.length === 0) return [];
      const { assignments, unavailable } = await client.readAssignments(targets);
      if (unavailable.length > 0) options.onUnavailable?.(unavailable);
      return assignments;
    },
    async assign(target, sourceId, expectedRevision) {
      try {
        return await client.assign(target, sourceId, expectedRevision);
      } catch (error) {
        if (error instanceof CardAnnotationClientError && error.status === 409) {
          const latest = client.conflictAssignment(error, target);
          if (latest) throw new CardSourceWriteError('conflict', latest);
        }
        if (error instanceof CardAnnotationClientError && error.status === 403) {
          reportRefusedTarget(error, target, options);
          throw new CardSourceWriteError('forbidden');
        }
        if (error instanceof CardAnnotationClientError && error.code === 'CARD_SOURCE_NOT_FOUND') throw new CardSourceWriteError('unknown_source');
        throw error;
      }
    },
  };
}

/**
 * The `scope` to hand the Notes/Source providers for a page showing cards of
 * `venuesOnPage`: the server's token (it covers the venues it can resolve),
 * or null when the page shows no venue or the server issued none. A card of a
 * venue the token does not cover is disabled per target (`onUnavailable`),
 * and the server refuses its writes, so the partial token is safe to hand out.
 */
export function pageScopeFor(result: CardScopeResult, venuesOnPage: readonly CardVenue[]): string | null {
  if (result.scope === null || venuesOnPage.length === 0) return null;
  return result.scope;
}
